import { useState, useEffect, useRef } from 'react';
import { marked } from 'marked';

marked.use({ breaks: true, gfm: true });

const W_DEFAULT = 200;
const H_DEFAULT = 140;
const HANDLE_H = 22;

const COLORS = {
  yellow: { fill: '#fef9c3', stroke: '#d97706', text: '#92400e' },
  green:  { fill: '#dcfce7', stroke: '#16a34a', text: '#14532d' },
  red:    { fill: '#fee2e2', stroke: '#ef4444', text: '#7f1d1d' },
};

// Icone Lucide scalate da viewBox 24×24
const SvgIcon = ({ cx, cy, size = 12, color, strokeWidth = 1.8, children }) => {
  const scale = size / 24;
  return (
    <g
      transform={`translate(${cx - size / 2}, ${cy - size / 2}) scale(${scale})`}
      fill="none" stroke={color}
      strokeWidth={strokeWidth / scale}
      strokeLinecap="round" strokeLinejoin="round"
      style={{ pointerEvents: 'none' }}
    >
      {children}
    </g>
  );
};

const IconChevronDown = (props) => (
  <SvgIcon {...props}><polyline points="6 9 12 15 18 9" /></SvgIcon>
);
const IconChevronUp = (props) => (
  <SvgIcon {...props}><polyline points="18 15 12 9 6 15" /></SvgIcon>
);
const IconTrash = (props) => (
  <SvgIcon {...props}>
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2" />
    <path d="M5 6l1.5 13a1 1 0 001 .9h9a1 1 0 001-.9L19 6" />
  </SvgIcon>
);

export const PostItItem = ({ postIt, onDragStart, onDelete, onTextChange, onColorChange, onResizeStart, onExitPanMode, supportsPointer = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(!postIt.text);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const textareaRef = useRef(null);

  const c = COLORS[postIt.color] || COLORS.yellow;
  const { x, y } = postIt;
  const w = postIt.w || W_DEFAULT;
  const h = postIt.h || H_DEFAULT;
  const displayH = isCollapsed ? HANDLE_H : h;
  const innerH = h - HANDLE_H - 28;

  // Bottoni handle (da destra): trash, collapse
  const trashCx = x + w - 12;
  const collapseCx = x + w - 34;
  const btnCy = y + HANDLE_H / 2;

  useEffect(() => {
    if (isEditing && textareaRef.current) textareaRef.current.focus();
  }, [isEditing]);

  const applyInlineFormat = (startToken, endToken = startToken) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { selectionStart, selectionEnd, value } = textarea;
    const selectedText = value.slice(selectionStart, selectionEnd);
    const wrappedText = `${startToken}${selectedText}${endToken}`;
    const nextValue = `${value.slice(0, selectionStart)}${wrappedText}${value.slice(selectionEnd)}`;

    onTextChange(postIt.id, nextValue);

    const cursorStart = selectionStart + startToken.length;
    const cursorEnd = cursorStart + selectedText.length;
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(cursorStart, cursorEnd);
    });
  };

  const handleEditorKeyDown = (e) => {
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return;

    const key = e.key.toLowerCase();
    if (key === 'b') {
      e.preventDefault();
      applyInlineFormat('**');
    } else if (key === 'i') {
      e.preventDefault();
      applyInlineFormat('*');
    } else if (key === 'u') {
      e.preventDefault();
      applyInlineFormat('<u>', '</u>');
    }
  };

  const handleTextareaBlur = () => {
    if (postIt.text.trim()) setIsEditing(false);
  };

  const toggleCollapse = (e) => {
    e.stopPropagation();
    setIsCollapsed(prev => !prev);
    if (isEditing) setIsEditing(false);
  };

  const handlePointerDragStart = (e) => {
    if (e.pointerType && !['mouse', 'pen', 'touch'].includes(e.pointerType)) return;
    e.stopPropagation();
    onDragStart(postIt.id, e);
  };

  const handlePointerResizeStart = (e) => {
    if (e.pointerType && !['mouse', 'pen', 'touch'].includes(e.pointerType)) return;
    e.stopPropagation();
    onResizeStart(postIt.id, e);
  };

  const maxChars = Math.max(10, Math.floor((w - 90) / 8));
  const collapsedPreview = postIt.text ? postIt.text.replace(/\n/g, ' ') : '';
  const truncatedText = postIt.text
    ? collapsedPreview.substring(0, maxChars) +
      (collapsedPreview.length > maxChars ? '…' : '')
    : '';
  const stopEventPropagation = (e) => e.stopPropagation();

  return (
    <g
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setConfirmDelete(false); }}
    >
      {/* Shadow */}
      <rect
        x={x + 3} y={y + 5} width={w} height={displayH}
        fill="rgba(0,0,0,0.07)" style={{ pointerEvents: 'none' }}
      />

      {/* Body */}
      <rect
        x={x} y={y} width={w} height={displayH}
        fill={c.fill} stroke={c.stroke}
        strokeWidth={isHovered ? 1.5 : 0.8}
        style={{ pointerEvents: 'none' }}
      />

      {/* ── Handle ── */}
      {confirmDelete ? (
        <g>
          <rect x={x} y={y} width={w} height={HANDLE_H}
            fill="#fee2e2" stroke="#ef4444" strokeWidth={1}
            style={{ pointerEvents: 'none' }}
          />
          <text x={x + 8} y={btnCy} dominantBaseline="middle"
            fontSize={10} fill="#7f1d1d"
            style={{ pointerEvents: 'none', userSelect: 'none' }}>
            Eliminare?
          </text>
          <text x={x + w - 8} y={btnCy} textAnchor="end" dominantBaseline="middle"
            fontSize={10} fontWeight="bold" fill="#ef4444"
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={(e) => { e.stopPropagation(); onDelete(postIt.id); }}>
            Sì, elimina
          </text>
        </g>
      ) : (
        <g>
          {/* Drag rect */}
          <rect x={x} y={y} width={w} height={HANDLE_H}
            fill={`${c.stroke}18`} style={{ cursor: 'move' }}
            onMouseDown={!supportsPointer ? (e) => { e.stopPropagation(); onDragStart(postIt.id, e); } : undefined}
            onPointerDown={supportsPointer ? handlePointerDragStart : undefined}
            onTouchStart={(e) => {
              if (supportsPointer) return;
              if (e.touches.length === 1) {
                e.stopPropagation();
                onDragStart(postIt.id, { 
                  clientX: e.touches[0].clientX, 
                  clientY: e.touches[0].clientY,
                  stopPropagation: () => e.stopPropagation(),
                  preventDefault: () => e.preventDefault()
                });
              }
            }}
          />

          {/* Grip o anteprima */}
          {isCollapsed ? (
            <text x={x + 8} y={btnCy} dominantBaseline="middle"
              fontSize={13} fontWeight="700"
              letterSpacing="0.3" fill={c.text}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {truncatedText}
            </text>
          ) : (
            <text x={x + w / 2} y={btnCy + 1}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={9} fill={c.stroke} letterSpacing="3"
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              • • •
            </text>
          )}

          {/* Bottoni — su hover */}
          {isHovered && (
            <>
              {/* Collapse */}
              <g style={{ cursor: 'pointer' }} onClick={toggleCollapse}>
                <circle cx={collapseCx} cy={btnCy} r={10} fill="transparent" />
                {isCollapsed
                  ? <IconChevronUp cx={collapseCx} cy={btnCy} size={14} color={c.stroke} strokeWidth={2.2} />
                  : <IconChevronDown cx={collapseCx} cy={btnCy} size={14} color={c.stroke} strokeWidth={2.2} />
                }
              </g>

              {/* Delete */}
              <g style={{ cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}>
                <circle cx={trashCx} cy={btnCy} r={10} fill="transparent" />
                <IconTrash cx={trashCx} cy={btnCy} size={14} color="#ef4444" strokeWidth={2} />
              </g>
            </>
          )}
        </g>
      )}

      {/* Content area */}
      {!isCollapsed && (
        <foreignObject
          x={x + 12} y={y + HANDLE_H + 4} width={w - 24} height={innerH}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={stopEventPropagation}
        >
          {isEditing ? (
            <textarea
              ref={textareaRef}
              value={postIt.text}
              onChange={(e) => onTextChange(postIt.id, e.target.value)}
              onBlur={handleTextareaBlur}
              onPointerDown={stopEventPropagation}
              onKeyDown={handleEditorKeyDown}
              placeholder="Nota..."
              style={{
                width: '100%', height: '100%',
                background: 'transparent', border: 'none', outline: 'none',
                resize: 'none',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '13px', lineHeight: '1.5',
                color: c.text, cursor: 'text',
              }}
            />
          ) : (
            <div
              className="postit-md"
              style={{ color: c.text }}
              onPointerDown={stopEventPropagation}
              onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
              onDoubleClick={(e) => { e.stopPropagation(); onExitPanMode?.(); setIsEditing(true); }}
              dangerouslySetInnerHTML={{ __html: marked.parse(postIt.text) }}
            />
          )}
        </foreignObject>
      )}

      {/* Tooltip collasso */}
      {isCollapsed && isHovered && postIt.text && (
        <foreignObject x={x} y={y + HANDLE_H + 4}
          width={Math.max(w, 240)} height={200}
          style={{ overflow: 'visible', pointerEvents: 'none' }}>
          <div style={{
            background: 'var(--bg)', border: `1.5px solid ${c.stroke}`,
            borderRadius: '6px', padding: '8px 12px',
            fontSize: '12px', lineHeight: '1.5', color: c.text,
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
            whiteSpace: 'pre-wrap', maxHeight: '180px', overflow: 'hidden',
          }}>
            {postIt.text}
          </div>
        </foreignObject>
      )}

      {/* Color dots — sempre visibili, non sfuggono */}
      {!isCollapsed && (
        <>
          {Object.entries(COLORS).map(([key, col], i) => (
            <circle
              key={key}
              cx={x + 13 + i * 15} cy={y + h - 10}
              r={isHovered ? 6 : 4.5}
              fill={col.fill}
              stroke={postIt.color === key ? col.stroke : `${col.stroke}55`}
              strokeWidth={postIt.color === key ? 2 : 1}
              opacity={isHovered ? 1 : 0.65}
              style={{ cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); onColorChange(postIt.id, key); }}
            />
          ))}

          <polygon
            points={`${x + w},${y + h - 18} ${x + w - 18},${y + h} ${x + w},${y + h}`}
            fill={c.stroke} opacity={isHovered ? 0.55 : 0.2}
            style={{ cursor: 'nwse-resize' }}
            onMouseDown={!supportsPointer ? (e) => { e.stopPropagation(); onResizeStart(postIt.id, e); } : undefined}
            onPointerDown={supportsPointer ? handlePointerResizeStart : undefined}
            onTouchStart={(e) => {
              if (supportsPointer) return;
              if (e.touches.length === 1) {
                e.stopPropagation();
                onResizeStart(postIt.id, { 
                  clientX: e.touches[0].clientX, 
                  clientY: e.touches[0].clientY,
                  stopPropagation: () => e.stopPropagation(),
                  preventDefault: () => e.preventDefault()
                });
              }
            }}
          />
        </>
      )}
    </g>
  );
};
